import React, { FC, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import FeatherIcon from "feather-icons-react";
import { useTranslation } from "react-i18next";

import {
  useGetAssignedCompanies,
  useGetMultipleFormsSections,
  useGetMultipleFormsDraftData,
} from "@api";
import { Button } from "@components/base";
import { PdfDocument, useCsobQuestionnaireStore } from "@components/main/csob/hub";
import { useUserStore } from "@store/user";
import {
  downloadPdfBlob,
  generatePdfAssetQuestions,
} from "@components/main/csob/hub/Questionnaires";

const ExportAllButton: FC = () => {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const { forms } = useCsobQuestionnaireStore();

  const formIds = useMemo(
    () =>
      forms
        .filter((form) => form.formId !== "client-form-placeholder")
        .map((form) => form.formId as string),
    [forms],
  );

  const [loadData, setLoadData] = useState<boolean>(false);
  const [exportLoading, setExportLoading] = useState<boolean>(false);

  const { assignedCompanies } = useGetAssignedCompanies();
  const { formsSections } = useGetMultipleFormsSections(user, formIds, loadData);
  const { formsDraftData } = useGetMultipleFormsDraftData(user, formIds, loadData);

  const companyName = useMemo(() => {
    return (
      assignedCompanies?.find((company) => company.id === user?.activeCompanyId)
        ?.name || "N/A"
    );
  }, [assignedCompanies, user]);

  const handleExportAll = async (): Promise<void> => {
    setLoadData(true);
  };

  const exportAll = async (): Promise<void> => {
    const assetsData = forms
      .map((form, index) => {
        if (form.formId === "client-form-placeholder") return null;

        const sections = formsSections[index].data || [];
        const drafData = formsDraftData[index].data || [];
        const { questions, relevantYear } = generatePdfAssetQuestions(
          sections,
          drafData,
        );

        return {
          id: form.formId!,
          name: form.name,
          type: String(form.questionnaireType),
          isGroupAsset: form.isGroupAsset || false,
          isHedgingAsset: form.isHedgingAsset || false,
          dateCreated: form.createdAtDateTime || new Date().toISOString(),
          relevantYear,
          questions,
        };
      })
      .filter((asset): asset is NonNullable<typeof asset> => asset !== null);

    await downloadPdfBlob({
      fileName: `${companyName}_${dayjs().format("DDMMYYYY")}.pdf`,
      pdfDocument: (
        <PdfDocument assets={assetsData} companyName={companyName} user={user} />
      ),
    });
  };

  useEffect(() => {
    if (loadData) {
      setExportLoading(true);
      const formSectionsFetching = formsSections.some(
        (section) => section.isFetching,
      );
      const draftDatasFetching = formsDraftData.some((draft) => draft.isFetching);

      if (!formSectionsFetching && !draftDatasFetching) {
        setExportLoading(false);
        setLoadData(false);
        exportAll();
      }
    }
  }, [formsSections, formsDraftData, loadData]);

  return (
    <Button
      variant="text"
      onClick={handleExportAll}
      size="small"
      isLoading={exportLoading}
      startIcon={<FeatherIcon icon="download" />}
    >
      {t("csob.questionnaires.exportAll")}
    </Button>
  );
};

export default ExportAllButton;
